import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';

import { BREADCRUMB_CONFIG, BreadcrumbConfig } from './breadcrumb.config';
import { BreadcrumbAncestor, BreadcrumbItem, BreadcrumbValue } from './breadcrumb.types';

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
    private readonly router  = inject(Router);
    private readonly config  = inject<BreadcrumbConfig>(BREADCRUMB_CONFIG);

    /**
     * Updates on every completed navigation.
     * Used as reactive dependency so `items` recomputes automatically.
     */
    private readonly currentUrl = toSignal(
        this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            map(e => e.urlAfterRedirects)
        ),
        { initialValue: this.router.url }
    );

    /** Ordered breadcrumb items derived from the active route tree. */
    readonly items = computed<BreadcrumbItem[]>(() => {
        this.currentUrl(); // reactive dependency — recomputes on every NavigationEnd
        return this.buildBreadcrumb(this.router.routerState.snapshot.root);
    });

    /**
     * True when the breadcrumb should be displayed.
     * Requires at least 2 items (module + page) and no excluded-route match.
     */
    readonly visible = computed(() => {
        const url      = this.currentUrl() ?? '';
        const excluded = this.config.excludedRoutes ?? [];
        if (excluded.some(pattern => url.includes(pattern))) return false;
        return this.items().length >= (this.config.minItems ?? 2);
    });

    private buildBreadcrumb(
        snapshot: ActivatedRouteSnapshot,
        url    = '',
        crumbs : BreadcrumbItem[] = []
    ): BreadcrumbItem[] {
        for (const child of snapshot.children) {
            const segment = child.url.map(s => s.path).join('/');
            const nextUrl = segment ? `${url}/${segment}` : url;
            // Use routeConfig.data (own static data) instead of child.data (merged with
            // inherited parent data) — Angular 20 uses paramsInheritanceStrategy:'always'
            // which would otherwise cause all child routes to inherit the parent breadcrumb.
            const ownData = child.routeConfig?.data;
            const raw: BreadcrumbValue | undefined = ownData?.['breadcrumb'];
            const ancestors: BreadcrumbAncestor[] | undefined = ownData?.['breadcrumbAncestors'];

            // Inject virtual ancestor items for flat routes that represent a sub-level
            if (ancestors?.length) {
                for (const ancestor of ancestors) {
                    const parts = nextUrl.split('/');
                    const ancestorUrl = parts.slice(0, parts.length - ancestor.segmentsBack).join('/');
                    crumbs.push({ label: ancestor.label, url: ancestorUrl });
                }
            }

            // null is an explicit "no breadcrumb" override (suppresses componentless parent inheritance)
            if (raw != null) {
                const label = typeof raw === 'function' ? raw(child) : raw;
                crumbs.push({ label, url: nextUrl });
            }

            this.buildBreadcrumb(child, nextUrl, crumbs);
        }

        // Last item is the current page — not a navigable link
        if (crumbs.length > 0) {
            crumbs[crumbs.length - 1] = { ...crumbs[crumbs.length - 1], url: null };
        }

        return crumbs;
    }
}
